import type { PracticeLesson } from '@/types/practiceLesson'

const LEGACY_STORAGE_KEY = 'lexio.practiceLessonSession'

function storageKey(userId: string): string {
  return `lexio.practiceLessonSession.${userId}`
}

export type LessonSessionPhase = 'active' | 'summary'

export type LessonSessionResult = {
  checked: boolean
  correct: boolean | null
  selfRevealed?: boolean
}

export interface LessonSessionState {
  userId: string
  exerciseSetId: string
  lesson: PracticeLesson
  model?: string
  stepExerciseIds: string[]
  currentIndex: number
  answers: Record<string, string>
  results: Record<string, LessonSessionResult>
  phase: LessonSessionPhase
  /** Start of the current visible timing segment. */
  startedAt: number
  /** Visible study ms before the current segment (optional for legacy saves). */
  activeStudyMs?: number
  savedAt: number
}

export function saveLessonSession(userId: string, state: Omit<LessonSessionState, 'userId' | 'savedAt'>): void {
  try {
    const payload: LessonSessionState = { ...state, userId, savedAt: Date.now() }
    localStorage.setItem(storageKey(userId), JSON.stringify(payload))
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    // ignore quota errors
  }
}

export function loadLessonSession(userId: string): LessonSessionState | null {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as LessonSessionState
    if (parsed.userId !== userId) return null
    if (!parsed.exerciseSetId || !parsed.lesson?.sections || !Array.isArray(parsed.stepExerciseIds)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearLessonSession(userId?: string): void {
  try {
    if (userId) {
      localStorage.removeItem(storageKey(userId))
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function isResumableSession(
  session: LessonSessionState | null,
  userId: string,
): session is LessonSessionState {
  return (
    session != null &&
    session.userId === userId &&
    session.phase === 'active' &&
    session.stepExerciseIds.length > 0 &&
    Boolean(session.exerciseSetId)
  )
}

export function estimateMinutesRemaining(
  estimatedMinutes: number,
  totalSteps: number,
  completedSteps: number,
): number {
  if (totalSteps <= 0) return estimatedMinutes
  const remainingSteps = Math.max(0, totalSteps - completedSteps)
  if (remainingSteps === 0) return 0
  const minutesPerStep = estimatedMinutes / totalSteps
  return Math.max(1, Math.ceil(minutesPerStep * remainingSteps))
}
